import * as React from "react";
import * as S from "@styled";
import * as AppPath from "../model/AppPath";
import * as _ from "lodash";
import classnames from "classnames";
import { connect } from "react-redux";
import Select, { MultiSelect } from "shared/components/select/Select";
import { createSelector } from "reselect";


import { Option } from "../../shared/model/Option";
import { DispatchType, Utilities } from "../../shared/utilities";
import { FilterCategory, ListState } from "../model/ListState";
import { State } from "../model/State";
import { StylesConfig } from "react-select";
import { selectWithInput } from "shared/components/select/model";
import { useQuery } from "shared/hooks/useQueryParams";
import { TaskModel } from "processList/model/Search";
import { fetchFinish, FetchId, fetchStart, getFetchPath, fetchError } from "processList/actions/FetchActions";

export const Search: React.FC<SearchProps> = (props) => {
    const query = useQuery();

    const filterCategory = query.get("filterCategory") ?? FilterCategory.Text;
    const filter = query.get("filterText") ?? ""
    const agentGroup = AppPath.getAgentGroupEnvironmentFromPath();
    const processListType = getFetchPath();

    const [filterText, setFilterText] = React.useState(filter);
    const [currentOptions, setCurrentOptions] = React.useState<Option[]>(props.options ?? []);

    const [triggerOptions, setTriggerOption] = React.useState<ITriggerSearchResponse | null>(null);
    const [taskOptions, setTaskOptions] = React.useState<Option[]>([]);
    const [promotedVariableOptions, setPromotedVariableOptions] = React.useState<Option[]>([]);
    const [, setDebouncedState] = React.useState("");

    React.useEffect(() => {
        // Only altern the state if the filter is empty (the "Clear filters"-button clicked),
        // otherwise the debounce messes up the state
        if (filter === '') {
            setFilterText('');
        }
    }, [filter]);


    React.useEffect(() => {
        // Some search filters still come from redux state, set them here
        if (props.options != null) {
            setCurrentOptions(props.options);
        }
    }, [props.options]);

    React.useEffect(() => {
        const fetchAndSetTaskOptions = async () => {
            props.startFetch(FetchId.usedTasks);
            try {
                const usedTasks = await Utilities.getFetch<Array<string>>(`api/${processListType}/used-tasks/${agentGroup.environmentName}/${agentGroup.agentGroupName}`);
                const taskForFilter = getTasksForFilter(props.tasks, usedTasks);
                setTaskOptions(taskForFilter);
                setCurrentOptions(taskForFilter);
                props.stopFetch(FetchId.usedTasks);
            } catch (error) {
                props.fetchError(error);
            }
        }

        const fetchAndSetPromotedVariableOptions = async () => {
            props.startFetch(FetchId.usedPromotedVariables);
            try {
                const promotedVariables = await Utilities.getFetch<Array<string>>(`api/${processListType}/used-promoted-variables/${agentGroup.environmentName}/${agentGroup.agentGroupName}`);
                const promotedVariablesForFilter = getPromotedResultVariablesForFilter(promotedVariables);
                setPromotedVariableOptions(promotedVariablesForFilter);
                setCurrentOptions(promotedVariablesForFilter);
                props.stopFetch(FetchId.usedPromotedVariables);
            } catch (error) {
                props.fetchError(error);
            }
        }

        const fetchAndSetTriggerOptions = async (filterCategory: FilterCategory.TriggerName | FilterCategory.TriggerType) => {
            props.startFetch(FetchId.usedTriggers);
            try {
                const usedTriggers = await Utilities.getFetch<ITriggerSearchResponse>(`api/${processListType}/used-triggers/${agentGroup.environmentName}/${agentGroup.agentGroupName}`);
                setTriggerOption(usedTriggers);
                const optionModel = getTriggerForFilter(usedTriggers, filterCategory)
                setCurrentOptions(optionModel);
                props.stopFetch(FetchId.usedTriggers);
            } catch (error) {
                props.fetchError(error);
            }
        }

        if (agentGroup.agentGroupName == null) {
            return;
        }

        if (filterCategory === FilterCategory.Tasks && props.tasks.length != 0) {
            if (!taskOptions?.length) {
                fetchAndSetTaskOptions();
            }
            setCurrentOptions(taskOptions);
            return;
        }

        if (filterCategory === FilterCategory.PromotedResultVariables) {
            if (!promotedVariableOptions?.length) {
                fetchAndSetPromotedVariableOptions();
                return;
            }
            setCurrentOptions(promotedVariableOptions);

        }

        if (filterCategory === FilterCategory.TriggerName || filterCategory === FilterCategory.TriggerType) {
            if (triggerOptions == null) {
                fetchAndSetTriggerOptions(filterCategory);
                return;
            }
            const triggerNameOptions = getTriggerForFilter(triggerOptions, filterCategory)
            setCurrentOptions(triggerNameOptions);
        }

    }, [filterCategory, agentGroup.agentGroupName, props.tasks, processListType]);

    const isTagFilter = filterCategory === FilterCategory.Tags;
    const isTasksFilter = filterCategory === FilterCategory.Tasks;

    const debounce = React.useCallback(
        _.debounce(async (newFilterText: string) => {
            setDebouncedState(newFilterText);
            AppPath.replaceSearchParameters(AppPath.getNewUrlState({
                filterText: newFilterText,
                page: null
            }));
        }, 500),
        []
    );

    const handleFilterTextChange = (event: React.FormEvent<HTMLInputElement>) => {
        const newFilterText = event.currentTarget.value;
        setFilterText(newFilterText);
        debounce(newFilterText);
    };

    const handleCategoryChange = (option: Option<FilterCategory>) => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterCategory: option.value,
            filterText: null,
            page: null
        }));
    };
    const handleFilterOptionChange = (newFilter: Option) => {
        const newFilterText = newFilter && newFilter.value || "";
        setFilterText(newFilterText);
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterText: newFilterText,
            page: null
        }));
    };
    const handleFilterOptionChangeForMulti = (newFilter: Option[]) => {
        const joinedMultiValues = newFilter && newFilter.map((x) => x.value).join(",") || "";
        setFilterText(joinedMultiValues);
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterText: joinedMultiValues,
            page: null
        }));
    };

    function getTitle(): string | null {
        switch (filterCategory) {
            case FilterCategory.Text:
                return FRENDS.Localizations.Text;
            case FilterCategory.Tags:
                return FRENDS.Localizations.Tags;
            case FilterCategory.Tasks:
                return FRENDS.Localizations.Tasks;
            case FilterCategory.UsedBy:
                return FRENDS.Localizations.UsedByProcesses;
            case FilterCategory.Subprocesses:
                return FRENDS.Localizations.Subprocesses;
            case FilterCategory.TriggerName:
                return "Trigger name"
            case FilterCategory.TriggerType:
                return "Trigger type"
            case FilterCategory.ExecutionsSince:
                return FRENDS.Localizations.Executions;
            case FilterCategory.ErrorsSince:
                return FRENDS.Localizations.Errors;
            case FilterCategory.PromotedResultVariables:
                return FRENDS.Localizations.PromotedColumn;
            default:
                return filterCategory;
        }
    }

    const customStyles: StylesConfig<Option> = ({
        ...selectWithInput,
        container: (provided) => ({ ...provided, width: 400 })
    })

    const getEditor = (filterType: FilterCategory | string | null) => {
        switch (filterType) {
            case FilterCategory.Text:
                return <S.SearchInput
                    id="filter-text-selector"
                    value={filterText}
                    placeholder={FRENDS.Localizations.FilterByTextAndDescription}
                    onChange={handleFilterTextChange}
                    isFullWidth
                    style={{ width: "405px", borderRadius: "0px 25px 25px 0px" }}
                />;
            case FilterCategory.ErrorsSince:
            case FilterCategory.ExecutionsSince:
                return (
                    <>
                        <S.FormGroupExtension>
                            {FRENDS.Localizations.ExecutionsSincePrefix}
                        </S.FormGroupExtension>
                        <S.Input
                            id="filter-number-selector"
                            type="number"
                            value={filterText}
                            onChange={handleFilterTextChange}
                            borderRadius="0px"
                            noSideBorder={true}
                            style={{ width: "200px" }}
                        />
                        <S.FormGroupExtension last={true}>
                            {FRENDS.Localizations.ExecutionsSinceSuffix}
                        </S.FormGroupExtension>
                    </>
                );
            case FilterCategory.Tags:
                return <MultiSelect
                    className={classnames({ "tag-select": isTagFilter })}
                    classNamePrefix="react-select"
                    id="filter-tags-selector"
                    styles={customStyles}
                    key={filterType}
                    value={Utilities.valuesFromList(currentOptions, filter.split(","))}
                    placeholder={FRENDS.Localizations.Select}
                    options={currentOptions as Option[]}
                    onChange={handleFilterOptionChangeForMulti}
                    autoFocus={true}
                    borderRadius="0px 25px 25px 0px"
                    height="initial"
                />;
            case FilterCategory.Tasks:
                return <MultiSelect
                    className={classnames({ "task-select": isTasksFilter })}
                    classNamePrefix="react-select"
                    id="filter-tasks-selector"
                    styles={customStyles}
                    key={filterType}
                    value={Utilities.valuesFromList(currentOptions, filter.split(","))}
                    placeholder={FRENDS.Localizations.Select}
                    options={currentOptions as Option[]}
                    onChange={handleFilterOptionChangeForMulti}
                    autoFocus={true}
                    borderRadius="0px 25px 25px 0px"
                    height="initial"
                />;
            default:
                return <Select
                    classNamePrefix="react-select"
                    id={`filter-${filterType}-selector`}
                    key={filterType}
                    value={Utilities.valueFromId(currentOptions, filter)}
                    styles={customStyles}
                    placeholder={FRENDS.Localizations.Select}
                    options={currentOptions as Option[]}
                    isClearable={true}
                    onChange={handleFilterOptionChange}
                    autoFocus={true}
                    borderRadius="0px 25px 25px 0px"
                    height="initial"
                />;
        }
    };

    const filterCategories: Option[] = [
        {
            value: FilterCategory.Text,
            label: FRENDS.Localizations.Text
        },
        {
            value: FilterCategory.Tags,
            label: FRENDS.Localizations.Tags
        },
        {
            value: FilterCategory.Tasks,
            label: FRENDS.Localizations.Tasks
        },
        {
            value: FilterCategory.PromotedResultVariables,
            label: FRENDS.Localizations.PromotedColumn
        },
        ...AppPath.isSubprocessList() ? [{
            value: FilterCategory.UsedBy,
            label: FRENDS.Localizations.UsedByProcesses
        }] : [],
        {
            value: FilterCategory.Subprocesses,
            label: FRENDS.Localizations.Subprocesses
        },
        {
            value: FilterCategory.TriggerName,
            label: "Trigger name"
        },
        {
            value: FilterCategory.TriggerType,
            label: "Trigger type"
        },
        {
            value: FilterCategory.ExecutionsSince,
            label: FRENDS.Localizations.Executions
        },
        {
            value: FilterCategory.ErrorsSince,
            label: FRENDS.Localizations.Errors
        }
    ];

    return (
        <S.FormGroup id="search-container-selector">
            <S.FlexContainer title={getTitle() || undefined} style={{ flexWrap: 'nowrap' }}>
                <Select
                    styles={selectWithInput}
                    borderRadius="25px 0px 0px 25px"
                    id="filter-category-selector"
                    classNamePrefix="react-select"
                    options={filterCategories}
                    onChange={handleCategoryChange}
                    value={Utilities.valueFromId(filterCategories, filterCategory)}
                    height="initial"
                    borderWidthRight="0px !important"
                />
                {getEditor(filterCategory)}
            </S.FlexContainer>
        </S.FormGroup>
    );
};
interface ITriggerSearchResponse {
    names: Array<string>,
    types: Array<string>
}
interface SearchStateProps {
    options: Option[] | null;
    tasks: TaskModel[];
    listState: ListState;
}
interface SearchDispatchProps {
    startFetch: (action: FetchId) => void;
    stopFetch: (action: FetchId) => void;
    fetchError: (error: string) => void;
}
type SearchProps = SearchStateProps & SearchDispatchProps;

const mapStateToProps = (state: State): SearchStateProps => {
    return {
        options: getOptionGroupByCategory(state),
        tasks: state.tasks,
        listState: state.listState
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): SearchDispatchProps => {
    return {
        startFetch: (action: FetchId) => dispatch(fetchStart(action)),
        stopFetch: (action: FetchId) => dispatch(fetchFinish(action)),
        fetchError: (error: string) => dispatch(fetchError(error))
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(Search);

const getOptionGroupByCategory = (state: State): Option[] | null => {
    switch (AppPath.getSearchParameters().filterCategory) {
        case FilterCategory.Text:
        case FilterCategory.ExecutionsSince:
        case FilterCategory.ErrorsSince:
        case FilterCategory.Tasks:
        case FilterCategory.PromotedResultVariables:
        case FilterCategory.TriggerName:
        case FilterCategory.TriggerType:
            return null;
        case FilterCategory.UsedBy:
            return getAllProcessesForUsedByProcesses(state);
        case FilterCategory.Subprocesses:
            return getAllProcessesForUsedProcesses(state);
        case FilterCategory.Tags:
            return getTagsForFilter(state);
        default:
            return null;
    }
};

const getTags = (state: Partial<State>) => state.availableTags;
const getProcessLookup = (state: Partial<State>) => state.referencedProcessMap;
const getUsedSubprocesses = (state: Partial<State>) => state.usedProcessesMap;
const getUsedInProcesses = (state: Partial<State>) => state.usedInProcessesMap;


export const getTasksForFilter = (tasks: TaskModel[], usedTasks: Array<string>): Option[] => {
    if (usedTasks !== undefined) {
        const uniqueProcessTasks: string[] = _.union(...usedTasks.map((process) => {
            const tasksByProcess: string[] = JSON.parse(process);
            return tasksByProcess.map((task) => task.toLowerCase());
        }));
        const activeTasks = tasks?.filter((task) => uniqueProcessTasks.some((uri) => uri === task.uri.toLowerCase()));
        const options = activeTasks?.map((task) => ({ label: `${task.fullName}${task.isStandard ? " - NET Standard" : ""}`, value: task.uri }));
        const seen = new Set();
        const added = new Set();
        activeTasks?.forEach((task) => {
            const taskIdentifier = task.name + task.guid;
            if (seen.size === (seen.add(taskIdentifier).size)) { // If task id already exist in set size will not be incremented
                if (added.size !== (added.add(taskIdentifier).size)) { // If there are over 2 duplicates we only add the first duplicate occurance.
                    options?.push({ label: `${task.name}${task.isStandard ? " - NET Standard" : ""}`, value: task.guid });
                }
            }
        });
        return _.sortBy(options, ["label"]);
    }
    return [];
}

export const getPromotedResultVariablesForFilter = (promotedVariables: Array<string>): Option[] => {
    if (promotedVariables !== undefined) {
        const uniquePromotedVariables: string[] = _.union(...promotedVariables.map((p) => {
            const promotedVariableByProcess: string[] = JSON.parse(p);
            return promotedVariableByProcess.map((task) => task.toLowerCase());
        }));
        return uniquePromotedVariables.map((promotedVariable) => ({ label: promotedVariable, value: promotedVariable }));
    }
    return [];
}

export const getTriggerForFilter = (triggers: ITriggerSearchResponse | null, filterCategory: FilterCategory.TriggerName | FilterCategory.TriggerType) => {
    if (triggers != null) {
        const triggerTypeMap = filterCategory == FilterCategory.TriggerName ? triggers.names : triggers.types;
        return triggerTypeMap.map((trigger) => ({ label: trigger, value: trigger }));
    }
    return [];
};

export const getTagsForFilter = createSelector(
    [getTags],
    (tags) => {
        if (tags !== undefined) {
            return tags.map((tag) => ({ label: tag, value: tag }));
        }
        return [];
    }
);

export const getAllProcessesForUsedByProcesses = createSelector(
    [getProcessLookup, getUsedInProcesses],
    (lookup, usedSubprocesses) => {
        if (lookup && usedSubprocesses) {
            const guids = Object.keys(usedSubprocesses);
            const result: Array<{ label: string, value: string }> = [];
            // Iterate guids that have subprocesses/processes
            guids.forEach(element => {
                const referencedProcess = Object.keys(usedSubprocesses[element]);
                // Iterate processes that should be the result for the search options
                referencedProcess.forEach((guid) => {
                    const processes = lookup[guid];
                    if (!processes?.length) {
                        return;
                    }

                    // There can be multiple versions of the process / subprocess calling or using this process.
                    // Join all the unique names for the label 
                    const label = Array.from(new Set(processes.map((x) => x.processName))).join(', ');
                    // The guid used for the actual search will be the same regadless of version of process
                    const value = element
                    // filter out duplicates
                    if (!result.some((x) => x.label === label)) {
                        result.push({ label: label, value: value })
                    } else {
                        const found = result.find(x => x.label === label);
                        if (found) {
                            found.value = found?.value + "," + value;
                        }
                    }
                })

            });
            return result;
        }
        return [];
    }
);

export const getAllProcessesForUsedProcesses = createSelector(
    [getProcessLookup, getUsedSubprocesses],
    (lookup, usedSubprocesses) => {
        if (lookup && usedSubprocesses) {
            const guids = Object.keys(usedSubprocesses);
            const result: Array<{ label: string, value: string }> = [];
            // Iterate guids that have subprocesses/processes
            guids.forEach(element => {
                const referencedProcess = Object.keys(usedSubprocesses[element]);
                // Iterate processes that should be the result for the search options
                referencedProcess.forEach((guid) => {
                    const processes = lookup[guid];
                    if (!processes?.length) {
                        return;
                    }

                    // There can be multiple versions of the process / subprocess calling or using this process.
                    // Join all the unique names for the label 
                    const label = Array.from(new Set(processes.map((x) => x.processName))).join(', ');
                    // The guid used for the actual search will be the same regadless of version of process
                    const value = processes[0].processGuid;
                    // filter out duplicates
                    if (!result.some((x) => x.label === label)) {
                        result.push({ label: label, value: value })
                    }
                })

            });
            return result;
        }
        return [];
    }
);
